import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

export default function AdminPending() {
    const qc = useQueryClient();
    const [rejectFor, setRejectFor] = useState(null);
    const [reason, setReason] = useState("");

    const { data: users = [], isLoading } = useQuery({
        queryKey: ["admin-users", "pending"],
        queryFn: async () => (await api.get("/admin/users", { params: { status_filter: "pending" } })).data,
    });

    const approve = useMutation({
        mutationFn: (id) => api.post(`/admin/users/${id}/approve`),
        onSuccess: () => {
            toast.success("Compte approuvé, un e-mail a été envoyé.");
            qc.invalidateQueries({ queryKey: ["admin-users"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const reject = useMutation({
        mutationFn: ({ id, reason }) => api.post(`/admin/users/${id}/reject`, { reason }),
        onSuccess: () => {
            toast.success("Demande refusée");
            setRejectFor(null);
            setReason("");
            qc.invalidateQueries({ queryKey: ["admin-users"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    return (
        <div data-testid="admin-pending">
            <h2 className="font-serif text-2xl text-[#D4BC7C]">Demandes en attente</h2>
            <p className="text-sm text-[#A0A0A0] mt-2">
                Vérifiez et validez les nouvelles demandes d'accès professionnel.
            </p>

            <div className="mt-8">
                {isLoading && <p className="text-[#A0A0A0]">Chargement...</p>}
                {!isLoading && users.length === 0 && (
                    <div className="bc-card text-center py-14" data-testid="admin-pending-empty">
                        <p className="font-serif text-[#D4BC7C] text-2xl">Aucune demande en attente</p>
                        <p className="text-sm text-[#A0A0A0] mt-2">Toutes les demandes ont été traitées.</p>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-4">
                    {users.map((u) => (
                        <div key={u.id} className="bc-card" data-testid={`pending-${u.id}`}>
                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h3 className="font-serif text-[#D4BC7C] text-xl">
                                            {u.prenom} {u.nom}
                                        </h3>
                                        <span className="text-xs uppercase tracking-widest text-[#A0A0A0]">
                                            {u.fonction}
                                        </span>
                                    </div>
                                    <p className="text-sm text-[#F2ECE0]/80 mt-1">{u.raison_sociale}</p>
                                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8 text-sm">
                                        <div>
                                            <span className="bc-label-small text-[10px]">E-mail</span>
                                            <p className="text-[#F2ECE0]">{u.email}</p>
                                        </div>
                                        <div>
                                            <span className="bc-label-small text-[10px]">Téléphone</span>
                                            <p className="text-[#F2ECE0]">{u.telephone}</p>
                                        </div>
                                        <div>
                                            <span className="bc-label-small text-[10px]">IDE / TVA</span>
                                            <p className="text-[#F2ECE0]">{u.ide_tva}</p>
                                        </div>
                                        <div>
                                            <span className="bc-label-small text-[10px]">Demande</span>
                                            <p className="text-[#F2ECE0]">
                                                {new Date(u.created_at).toLocaleDateString("fr-CH")}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[180px]">
                                    <button
                                        onClick={() => approve.mutate(u.id)}
                                        disabled={approve.isPending}
                                        className="bc-btn bc-btn--sm"
                                        data-testid={`approve-${u.id}`}
                                    >
                                        <Check size={14} /> Approuver
                                    </button>
                                    <button
                                        onClick={() => setRejectFor(u.id)}
                                        className="bc-btn bc-btn--sm bc-btn--danger"
                                        data-testid={`reject-${u.id}`}
                                    >
                                        <X size={14} /> Refuser
                                    </button>
                                </div>
                            </div>

                            {rejectFor === u.id && (
                                <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--bc-border)" }}>
                                    <label className="bc-label">Motif du refus (optionnel, transmis par e-mail)</label>
                                    <textarea
                                        className="bc-input"
                                        rows={3}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        data-testid={`reject-reason-${u.id}`}
                                    />
                                    <div className="mt-4 flex gap-3">
                                        <button
                                            className="bc-btn bc-btn--sm bc-btn--danger"
                                            onClick={() => reject.mutate({ id: u.id, reason })}
                                            disabled={reject.isPending}
                                            data-testid={`confirm-reject-${u.id}`}
                                        >
                                            Confirmer le refus
                                        </button>
                                        <button
                                            className="bc-btn bc-btn--sm bc-btn--ghost"
                                            onClick={() => {
                                                setRejectFor(null);
                                                setReason("");
                                            }}
                                        >
                                            Annuler
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
